import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { fetchAlerts, markAlertRead, markAllAlertsRead, deleteAlert } from "../lib/api";
import { Bell, ArrowLeft, CaretUp, CaretDown, Trash, Check, Sparkle } from "@phosphor-icons/react";
import { formatPct, formatSignedPct } from "../lib/format";
import ProtectedRoute from "../auth/ProtectedRoute";
import BetInline from "../components/BetInline";

function AlertsInner() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const d = await fetchAlerts(200);
      setAlerts(d.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="alerts-page">
      <Header />
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell size={22} weight="duotone" className="text-edge" />
            <h1 className="font-display text-3xl font-medium">AI-Edge Alerts</h1>
          </div>
          <button
            data-testid="mark-all-read-btn"
            onClick={async () => { await markAllAlertsRead(); load(); }}
            className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <Check size={12} /> Mark all read
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading…</div>
        ) : alerts.length === 0 ? (
          <div className="border border-white/10 p-10 text-center text-zinc-500 font-body">
            <Sparkle size={28} weight="duotone" className="mx-auto mb-3 text-edge" />
            No alerts yet. MFSMAI Oracle scans top markets every 2 hours — when it finds a market
            mispriced by your threshold, it'll show up here.
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5">
            {alerts.map((a) => (
              <div
                key={a.alert_id}
                data-testid={`alert-row-${a.alert_id}`}
                className={`flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${
                  !a.read ? "bg-edge/5" : ""
                }`}
              >
                {a.image && <img src={a.image} alt="" className="w-10 h-10 object-cover border border-white/10" />}
                <button
                  className="flex-1 min-w-0 text-left"
                  onClick={async () => {
                    if (!a.read) await markAlertRead(a.alert_id);
                    navigate(`/market/${a.marketId}`);
                  }}
                >
                  <div className="font-display text-sm font-medium line-clamp-2">{a.question}</div>
                  <div className="mt-1 flex items-center gap-4 text-[11px] font-mono tabular-nums text-zinc-500">
                    <span>MKT {formatPct(a.marketYesPrice, 0)}</span>
                    <span className="text-edge">AI {formatPct(a.probability, 0)}</span>
                    <span className={`inline-flex items-center gap-0.5 ${a.edge >= 0 ? "text-profit" : "text-loss"}`}>
                      {a.edge >= 0 ? <CaretUp size={9} weight="fill" /> : <CaretDown size={9} weight="fill" />}
                      {formatSignedPct(a.edge, 1)} edge
                    </span>
                    <span>{a.verdict}</span>
                    <span>{new Date(a.createdAt).toLocaleString()}</span>
                  </div>
                </button>
                <BetInline slug={a.slug} marketId={a.marketId} edge={a.edge} />
                <button
                  data-testid={`delete-alert-${a.alert_id}`}
                  onClick={async () => { await deleteAlert(a.alert_id); load(); }}
                  className="text-zinc-500 hover:text-loss"
                  title="Delete"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Alerts() {
  return (
    <ProtectedRoute>
      <AlertsInner />
    </ProtectedRoute>
  );
}
